import { motion } from "framer-motion";
import { Check, Clock, Box, HardHat, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const PUMPS = [
  { type: "Line pump", price: 190, testid: "price-line-pump" },
  { type: "Boom pump", price: 220, testid: "price-boom-pump" },
];

const PERKS = ["4 hour minimum", "$8 per m³ pumped", "1 hour travel charge (in addition to minimum)"];

const TRUST = [
  { icon: Clock, label: "Minimum 4 hours", testid: "trust-minimum-hours" },
  { icon: Box, label: "$8 per m³ pumped", testid: "trust-per-m3" },
  { icon: HardHat, label: "Fully insured", testid: "trust-insured" },
  { icon: MapPin, label: "Servicing all areas", testid: "trust-all-areas" },
];

const reveal = {
  hidden: { opacity: 0, y: 48 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  },
};

export default function Pricing() {
  return (
    <section id="pricing" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mb-14 text-center sm:mb-16"
        >
          <h2 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight sm:text-6xl">
            Prices and<span className="text-[#0052FF]"> Rates</span>
          </h2>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-2">
          {PUMPS.map((p, i) => (
            <motion.div
              key={p.type}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.1 }}
              data-testid={p.testid}
              className="group border border-[#0052FF]/30 bg-[#121721] p-8 transition-all duration-300 hover:border-[#0052FF] hover:shadow-[0_0_40px_rgba(0,82,255,0.25)] sm:p-10"
            >
              <p className="font-tech text-xs uppercase tracking-[0.3em] text-slate-400">
                {p.type}
              </p>
              <div className="mt-3 flex items-end gap-2 font-display leading-none">
                <span className="text-7xl font-extrabold tracking-tight text-white sm:text-8xl">
                  ${p.price}
                </span>
                <span className="pb-2 text-3xl font-bold text-[#4D8DFF]">/hr</span>
              </div>
              <ul className="mt-8 space-y-3 border-t border-white/10 pt-6">
                {PERKS.map((perk) => (
                  <li
                    key={perk}
                    className="flex items-center gap-3 font-display text-lg font-bold uppercase tracking-wide text-white"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0052FF]">
                      <Check className="h-4 w-4 text-white" strokeWidth={3} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-8 text-center text-base text-slate-400"
        >
          Price will differ for specific jobs —{" "}
          <button
            onClick={() => scrollToId("#quote")}
            data-testid="pricing-quote-link"
            className="font-semibold text-[#4D8DFF] underline-offset-4 transition-colors hover:text-white hover:underline"
          >
            flick us the details for a sharp quote
          </button>
          .
        </motion.p>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          data-testid="trust-strip"
          className="mt-12 grid grid-cols-2 border border-white/10 bg-black/60 md:grid-cols-4"
        >
          {TRUST.map((t) => (
            <div
              key={t.label}
              data-testid={t.testid}
              className="flex items-center gap-4 border-white/10 px-6 py-6 [&:nth-child(odd)]:border-r md:border-r md:last:border-r-0"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#0052FF]">
                <t.icon className="h-5 w-5 text-white" />
              </span>
              <span className="font-display text-base font-bold uppercase tracking-wide text-white">
                {t.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}